import React, { useEffect, useState } from 'react';
import { colors, css, fonts} from '../../Styles';
import { progressColor } from '../../Definitions';
import { progressTextColor } from '../../Definitions/progressColor';
import { useTranslation } from 'react-i18next';
import { getProgressData } from './_ProgressViewer/getProgressData';
import FeedLabel from './SubComponents/feedLabel';
import { BarTypes, Progress_Props, ProgressTypes } from './_ProgressViewer/progressDefinitions';

interface ProgressBar_Props extends Progress_Props {
  progressType?: ProgressTypes;
  barType?: BarTypes;
  barWidth?: string;
  labels?: Progress_Props["labels"] & {
    penLabel?: string | number;
    noDataDisclaimer?: string;
  };
  extension?: Progress_Props["extension"] & {
    extensionOnly?: boolean;
  };
}
 
export default function ProgressBar ({ total = 0, progressed = 0, progressType = ProgressTypes.remaining, barType = BarTypes.primary, animation = false, labels, extension, barWidth }: ProgressBar_Props) {
  const { t } = useTranslation();
  const [animate, setAnimate] = useState(false);
  const { percent, text } = getProgressData(progressed, total, progressType);

  useEffect(() => {
    setAnimate(true);
  }, []);

  if (progressed === 0 && total === 0 && !labels?.penLabel) {
    return <p style={{ ...css.emptyPen, opacity: "0.5" }}>
      { labels?.noDataDisclaimer ? labels.noDataDisclaimer : t("noData", {defaultValue: "No Data"})}
    </p>
  }

  const tooMuchFed = progressed > total;

  const negative = tooMuchFed && (
    <FeedLabel labelColor={colors.systemRed} contentString={`+${Math.abs((total-progressed)/1000).toFixed(2)}`}/>
  );

  // #region | Progress Bar JSX
  const progressAnimation = animation ? animate ? `${percent}%` : "0%" : `${percent}%`;

  const progressBar = (
    <div style={{
      width: barWidth ? barWidth : barType === BarTypes.primary ? "100%" : "80px",
      backgroundColor: barType === BarTypes.primary ? colors.greyTertiary : colors.greyLightSecondary,
      height: barType === BarTypes.primary ? "15px" : "30px",
      position: "relative",
      borderRadius: barType === BarTypes.primary ? "10px" : "0px",
      overflow: "hidden",
      pointerEvents: "none"
    }}>
      <div style={{
        backgroundColor: progressColor(percent),
        height: "100%",
        width: progressAnimation,
        position: "absolute",
        left: "0px",
        transition: "width 1s ease"
      }}/>
      { barType === BarTypes.primary
        ? <div style={{position: "absolute", display: "grid", gridTemplateColumns: "5fr 1fr 5fr", width: "100%", height: "100%"}}>
            <p style={{ ...css.progressBarLabel, justifyContent: "end" }}>{text}</p>
            <p style={{ ...css.progressBarLabel, justifyContent: "center" }}>/</p>
            <p style={{ ...css.progressBarLabel, justifyContent: "start" }}>{(total/1000).toFixed(2)}</p>
          </div>
        : <p style={{ ...css.progressBarText, position: "absolute", transform: "translateX(-50%)", left: "50%", color: progressTextColor(percent) }}>
            {labels?.penLabel}
          </p>
      }
    </div>
  );
  // #endregion

  return (
    labels?.header || labels?.unit ? (
      <div style={{width: barWidth ? barWidth : barType === BarTypes.primary ? "100%" : "80px", height: labels?.header&&labels?.unit ? "65px" : "fit-content", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", gap: !labels?.header&&labels?.unit ? "3px" : "9px"}}>
        { labels?.header ? <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary, textAlign: "center"}}>
          {labels.header}
        </p> : undefined}
        {progressBar}
        {labels?.unit ? labels.unit : undefined}
      </div>
    ) : (
      extension?.extensionOnly
        ? (tooMuchFed ? negative : null)
        : (tooMuchFed && extension?.hasExtension
          ? <div style={{ display: "flex", alignItems: "center", gap: "5px", zIndex: "99", position: "relative" }}>
              {progressBar}{negative}
            </div>
          : progressBar)
    )
  );
}
